import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatApiError } from "@/lib/api";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export const Login = () => {
  const { t, lang } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const user = await login(email, password);
      const dest = location.state?.from?.pathname || (user.role === "admin" ? "/admin" : "/dashboard");
      navigate(dest, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 sm:px-6 py-8 sm:py-12" data-testid="login-page">
      <Card className="w-full max-w-md p-6 sm:p-8 border border-slate-200 shadow-sm">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-start">
          {t("login_title")}
        </h1>
        <p className="text-sm text-slate-600 mb-8 text-start">
          {t("no_account")}{" "}
          <Link to="/register" className="text-blue-600 hover:underline" data-testid="login-go-register-link">
            {t("cta_register")}
          </Link>
        </p>

        {err && (
          <Alert variant="destructive" className="mb-4" data-testid="login-error-alert">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        <GoogleAuthButton testId="login-google-btn" />

        <div className="relative my-6 flex items-center" data-testid="login-divider">
          <div className="flex-1 border-t border-slate-200" />
          <span className="px-3 text-xs uppercase tracking-wide text-slate-400">
            {lang === "ar" ? "أو" : "or"}
          </span>
          <div className="flex-1 border-t border-slate-200" />
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label htmlFor="login-email" className="text-start block">{t("email_label")}</Label>
            <Input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="login-email-input"
              className="h-11"
              style={{ fontSize: "16px" }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-pass" className="text-start block">{t("password_label")}</Label>
            <Input
              id="login-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="login-password-input"
              className="h-11"
              style={{ fontSize: "16px" }}
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-slate-900 hover:bg-slate-800 h-12 text-base"
            data-testid="login-submit-btn"
          >
            {busy ? t("loading") : t("submit_login")}
          </Button>
        </form>
      </Card>
    </main>
  );
};

export default Login;
